import React, { useState, useCallback, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { CLUBS } from '@/data/clubDemoData';
import { ArrowRight, ArrowLeft, ChevronDown, LinkIcon, Check, Info } from 'lucide-react';
import { MR, JB } from '@/components/club-demo/DemoShared';
import { RealityCheck } from '@/components/club-demo/RealityCheck';
import { Drivers } from '@/components/club-demo/DriversSection';
import { Benchmark } from '@/components/club-demo/BenchmarkSection';
import { HiddenRevenue } from '@/components/club-demo/RevenueSection';
import { Actions, FinalCTA } from '@/components/club-demo/CTASections';
import { PilotModal } from '@/components/club-demo/PilotModal';
import { useLanguage } from '@/i18n/LanguageContext';
import { demoTranslations, clubTextOverrides_es } from '@/i18n/demoTranslations';

function useLocalizedClub(club, lang) {
  return useMemo(() => {
    if (lang !== 'es' || !club) return club;
    const ov = clubTextOverrides_es[club.id];
    if (!ov) return club;
    return {
      ...club,
      trendLabel: ov.trendLabel || club.trendLabel,
      scoreContext: ov.scoreContext || club.scoreContext,
      peerGap: ov.peerGap || club.peerGap,
      driverInsights: ov.driverInsights || club.driverInsights,
      benchmarkInsight: ov.benchmarkInsight || club.benchmarkInsight,
      realityInsight: ov.realityInsight || club.realityInsight,
      driverKeyInsight: ov.driverKeyInsight || club.driverKeyInsight,
      revenueInsight: ov.revenueInsight || club.revenueInsight,
      revenue: {
        ...club.revenue,
        international: { ...club.revenue.international, ...(ov.revenue?.international || {}) },
        sponsorship: { ...club.revenue.sponsorship, ...(ov.revenue?.sponsorship || {}) },
        digital: { ...club.revenue.digital, ...(ov.revenue?.digital || {}) },
      },
      actions: ov.actions || club.actions,
    };
  }, [club, lang]);
}

export default function ClubDemo() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { lang, setLang } = useLanguage();
  const dt = demoTranslations[lang] || demoTranslations.en;

  const initialClub = CLUBS.find((c) => c.id === searchParams.get('club'))?.id || 'club-a';
  const [clubId, setClubId] = useState(initialClub);
  const [step, setStep] = useState(0);
  const club = useMemo(() => CLUBS.find((c) => c.id === clubId), [clubId]);
  const localizedClub = useLocalizedClub(club, lang);

  const goNext = useCallback(() => setStep((s) => Math.min(s + 1, dt.sections.length - 1)), [dt.sections.length]);
  const goBack = useCallback(() => setStep((s) => Math.max(s - 1, 0)), []);
  const selectClub = useCallback((id) => {
    setClubId(id);
    setStep(0);
    setSearchParams({ club: id }, { replace: true });
  }, [setSearchParams]);

  const [pilotOpen, setPilotOpen] = useState(false);
  const openPilot = useCallback(() => setPilotOpen(true), []);
  const closePilot = useCallback(() => setPilotOpen(false), []);

  const section = dt.sections[step];

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <DemoHeader clubId={clubId} dt={dt} lang={lang} setLang={setLang} />
      <DemoDisclaimer text={dt.disclaimer} />
      <SelectorBar clubs={CLUBS} activeId={clubId} onSelect={selectClub} steps={dt.sections} current={step} />
      <main className="flex-1 max-w-[1100px] w-full mx-auto px-4 md:px-8 py-10 md:py-14">
        {section.id === 'reality' && <RealityCheck club={localizedClub} dt={dt} />}
        {section.id === 'drivers' && <Drivers club={localizedClub} dt={dt} />}
        {section.id === 'benchmark' && <Benchmark club={localizedClub} clubs={CLUBS} dt={dt} lang={lang} />}
        {section.id === 'revenue' && <HiddenRevenue club={localizedClub} dt={dt} onExplorePilot={openPilot} />}
        {section.id === 'actions' && <Actions club={localizedClub} dt={dt} />}
        {section.id === 'cta' && <FinalCTA club={localizedClub} dt={dt} onExplorePilot={openPilot} />}
      </main>
      {section.id !== 'cta' && (
        <NavBar step={step} goBack={goBack} goNext={goNext} dt={dt} />
      )}
      <PilotModal open={pilotOpen} onClose={closePilot} clubName={localizedClub.name} dt={dt} />
    </div>
  );
}

function DemoHeader({ clubId, dt, lang, setLang }) {
  const [copied, setCopied] = useState(false);
  const shareUrl = `${window.location.origin}/club-demo?club=${clubId}`;

  const copyLink = useCallback(() => {
    const url = shareUrl;
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(url).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }).catch(() => fallbackCopy(url));
    } else {
      fallbackCopy(url);
    }
    function fallbackCopy(text) {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.cssText = 'position:fixed;opacity:0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [shareUrl]);

  return (
    <header className="border-b border-white/[0.06] bg-[hsl(220,15%,8%)]/80 backdrop-blur-xl sticky top-0 z-50">
      <div className="max-w-[1100px] mx-auto px-4 md:px-8 h-14 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5" data-testid="demo-logo">
          <div className="w-8 h-8 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center">
            <svg className="w-3.5 h-3.5 text-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-label="YBV logo"><line x1="12" y1="2" x2="12" y2="22"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
          </div>
          <span className="text-[14px] font-semibold tracking-tight" style={MR}>YBV</span>
          <span className="text-[11px] text-muted-foreground/60 tracking-wide ml-1 hidden sm:inline" style={JB}>{dt.headerLabel}</span>
        </Link>
        <div className="flex items-center gap-2">
          <DemoLangToggle lang={lang} setLang={setLang} />
          <Button
            variant="ghost"
            size="sm"
            onClick={copyLink}
            className={`text-[12px] h-7 px-3 gap-1.5 rounded-full transition-colors ${copied ? 'text-[hsl(150,50%,45%)]' : 'text-muted-foreground hover:text-foreground'}`}
            data-testid="share-link-btn"
          >
            {copied ? <Check className="w-3 h-3" /> : <LinkIcon className="w-3 h-3" />}
            {copied ? dt.copied : dt.share}
          </Button>
          <Link to="/briefing">
            <Button size="sm" className="bg-primary hover:bg-primary/90 text-[12px] font-medium h-7 px-3 rounded-full" data-testid="demo-header-cta">
              {dt.requestBriefing} <ArrowRight className="w-3 h-3 ml-1" />
            </Button>
          </Link>
        </div>
      </div>
    </header>
  );
}

function DemoLangToggle({ lang, setLang }) {
  return (
    <div className="flex items-center rounded-full border border-border/40 bg-card/40 overflow-hidden h-7" data-testid="demo-lang-toggle">
      {['en', 'es'].map((l) => (
        <button
          key={l}
          onClick={() => setLang(l)}
          className={`px-2.5 h-full text-[11px] font-semibold tracking-wider uppercase transition-colors ${
            lang === l
              ? 'bg-primary/15 text-primary'
              : 'text-muted-foreground/60 hover:text-muted-foreground'
          }`}
          style={MR}
          data-testid={`demo-lang-${l}`}
        >
          {l}
        </button>
      ))}
    </div>
  );
}

function SelectorBar({ clubs, activeId, onSelect, steps, current }) {
  return (
    <div className="border-b border-border/40 bg-card/30">
      <div className="max-w-[1100px] mx-auto px-4 md:px-8 py-4 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
        <ClubSelector clubs={clubs} activeId={activeId} onSelect={onSelect} />
        <ProgressBar steps={steps} current={current} />
      </div>
    </div>
  );
}

function ClubSelector({ clubs, activeId, onSelect }) {
  const [open, setOpen] = useState(false);
  const active = clubs.find((c) => c.id === activeId);
  return (
    <div className="relative" data-testid="club-selector">
      <button onClick={() => setOpen(!open)} className="flex items-center gap-2.5 rounded-lg border border-border/50 bg-card/60 px-4 py-2 min-w-[200px] hover:border-border transition-colors">
        <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: active.color }} />
        <span className="text-sm font-medium text-foreground" style={MR}>{active.name}</span>
        <ChevronDown className={`w-4 h-4 text-muted-foreground ml-auto transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 w-full rounded-lg border border-border/50 bg-card shadow-xl z-40 py-1 overflow-hidden">
          {clubs.map((c) => (
            <button key={c.id} onClick={() => { onSelect(c.id); setOpen(false); }} className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-left text-sm transition-colors ${c.id === activeId ? 'bg-primary/10 text-primary' : 'text-foreground/80 hover:bg-card-elevated'}`} data-testid={`club-option-${c.id}`}>
              <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: c.color }} /> {c.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ProgressBar({ steps, current }) {
  return (
    <div className="flex items-center gap-1.5 flex-1 min-w-0 overflow-x-auto scrollbar-hide" data-testid="demo-progress">
      {steps.map((s, i) => (
        <div key={s.id} className="flex items-center gap-1.5 flex-shrink-0">
          {i > 0 && <div className={`w-4 h-px ${i <= current ? 'bg-primary/50' : 'bg-border/40'}`} />}
          <span className={`text-[11px] whitespace-nowrap px-2 py-1 rounded-full transition-colors ${i === current ? 'bg-primary/15 text-primary font-medium' : i < current ? 'text-primary/60' : 'text-muted-foreground/40'}`} style={JB}>{s.label}</span>
        </div>
      ))}
    </div>
  );
}

function NavBar({ step, goBack, goNext, dt }) {
  return (
    <div className="border-t border-border/40 bg-card/30">
      <div className="max-w-[1100px] mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
        <Button variant="ghost" onClick={goBack} disabled={step === 0} className="text-muted-foreground gap-1.5 disabled:opacity-30" data-testid="demo-back-btn">
          <ArrowLeft className="w-4 h-4" /> {dt.back}
        </Button>
        <Button onClick={goNext} className="bg-primary hover:bg-primary/90 font-medium gap-1.5 px-6" data-testid="demo-next-btn">
          {dt.continue} <ArrowRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

function DemoDisclaimer({ text }) {
  return (
    <div className="bg-[hsl(38,85%,55%)]/[0.06] border-b border-[hsl(38,85%,55%)]/15" data-testid="demo-disclaimer">
      <div className="max-w-[1100px] mx-auto px-4 md:px-8 py-2.5 flex items-start gap-2.5">
        <Info className="w-3.5 h-3.5 text-[hsl(38,85%,55%)]/70 flex-shrink-0 mt-0.5" />
        <p className="text-[11px] text-[hsl(38,85%,55%)]/80 leading-relaxed" style={JB}>{text}</p>
      </div>
    </div>
  );
}
